import {
  computed,
  inject,
  provide,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from 'vue';

export type FileManagerNavigationState = {
  currentPath: Readonly<Ref<string>>;
  expandedPaths: Ref<Set<string>>;
  navigateTo: (path: string, opts?: { replace?: boolean }) => void;
  toggleExpanded: (path: string) => void;
};

export const fileManagerNavigationKey: InjectionKey<FileManagerNavigationState> =
  Symbol('fileManagerNavigation');

const ancestorsOf = (path: string): string[] => {
  const segments = path.split('/').filter(Boolean).slice(0, -1);
  const ancestors: string[] = ['/'];
  let current = '';
  for (const segment of segments) {
    current += `/${segment}`;
    ancestors.push(current);
  }
  return ancestors;
};

export const provideFileManagerNavigation = (
  path: Ref<string> | ComputedRef<string>,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
) => {
  const currentPath = computed(() => toValue(path));
  const expandedPaths = ref(new Set<string>());

  watch(
    currentPath,
    (newPath) => {
      expandedPaths.value = new Set([
        ...expandedPaths.value,
        ...ancestorsOf(newPath),
      ]);
    },
    { immediate: true, flush: 'sync' },
  );

  const navigateTo = (path: string, opts?: { replace?: boolean }) => {
    navigate(path, opts);
  };

  const toggleExpanded = (path: string) => {
    const next = new Set(expandedPaths.value);
    if (next.has(path)) {
      next.delete(path);
    } else {
      next.add(path);
    }
    expandedPaths.value = next;
  };

  const state: FileManagerNavigationState = {
    currentPath,
    expandedPaths,
    navigateTo,
    toggleExpanded,
  };
  provide(fileManagerNavigationKey, state);
  return state;
};

export const useFileManagerNavigation = () => {
  const state = inject(fileManagerNavigationKey);
  if (!state) {
    throw new Error(
      'useFileManagerNavigation() must be called within a component tree provided by provideFileManagerNavigation()',
    );
  }
  return state;
};
